import { useApolloClient, useReactiveVar } from '@apollo/client';
import { useEffect, useRef, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { activeQuestCompletions } from '../../client/apollo';
import { CompleteQuestMutation } from '../../client/generated/graphql';
import { completeQuestCache } from '../../client/mutations/completeQuest';
import { ListQuestCompletion } from '../../client/types';
import { AnalyticsEventType } from '../../contexts/analyticsEvents';
import { QUEST_COMPLETE_ANIMATION_DELAY } from '../../helpers/animation';
import { useAnalytics } from '../../hooks/useAnalytics';

export const QuestCompletionHandler: FC = () => {
    const activeCompletions = useReactiveVar(activeQuestCompletions);
    const client = useApolloClient();
    const navigate = useNavigate();
    const { recordEvent } = useAnalytics();
    const processingRef = useRef<Set<string>>(new Set());

    const createMutationResult = (completion: ListQuestCompletion) => ({
        data: {
            completeQuest: completion
        } as CompleteQuestMutation
    });

    const updateCache = (completion: ListQuestCompletion) => {
        if (!client) return;
        if (!completion.eventLog) return;
        const mutationResult = createMutationResult(completion);
        completeQuestCache(client.cache, mutationResult);
    };

    const navigateToAchievements = (achievements: any[]) => {
        if (achievements.length > 0) {
            navigate('/completed-achievements', {
                state: {
                    achievements
                }
            });
        }
    };

    const removeCompletion = (questId: string) => {
        const current = activeQuestCompletions();
        current.delete(questId);
        activeQuestCompletions(new Map(current));
    };

    const processCompletion = (
        questId: string,
        completion: ListQuestCompletion
    ) => {
        recordEvent(AnalyticsEventType.QUEST_COMPLETED, {
            questId,
            categoryId: completion.quest.categoryId,
            xpEarned: completion.completedXp,
        });

        setTimeout(() => {
            updateCache(completion);
            
            const completedAchievements = completion.completedAchievements ?? [];
            navigateToAchievements(completedAchievements);
            removeCompletion(questId);
            processingRef.current.delete(questId);
        }, QUEST_COMPLETE_ANIMATION_DELAY);
    };

    useEffect(() => {
        activeCompletions.forEach((completion, questId) => {
            if (processingRef.current.has(questId)) {
                return;
            }
            
            processingRef.current.add(questId);
            processCompletion(questId, completion);
        });
    }, [activeCompletions, client, navigate]);

    return null;
};
